import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Collapse,
  Group,
  Loader,
  Modal,
  MultiSelect,
  NumberInput,
  Progress,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconChevronDown, IconChevronRight, IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../../api/client";
import type { Customer, DeliveryPlanRow } from "../../api/types";
import { ResponsiveTable } from "../responsiveTable/ResponsiveTable";
import { PlanBreakdown, sumBreakdown } from "./PlanBreakdown";

const NOW = new Date();
const CURRENT_YEAR = NOW.getFullYear();
const CURRENT_MONTH = NOW.getMonth() + 1; // 1..12
const MATRIX_CUSTOMERS_KEY = "deliveryPlan.matrixCustomers";
const MATRIX_HIDDEN_COLS_KEY = "deliveryPlan.matrixHiddenCols";

function loadMatrixCustomers(): string[] {
  return loadStringList(MATRIX_CUSTOMERS_KEY);
}

function loadStringList(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.map(String) : [];
  } catch {
    return [];
  }
}

export function DeliveryPlanTab() {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<"cards" | "matrix">("cards");
  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [month, setMonth] = useState<number>(CURRENT_MONTH);
  const [productTypeId, setProductTypeId] = useState<string>("1"); // 1 = Modul, 2 = Antenă
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [rows, setRows] = useState<DeliveryPlanRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  // Customers to show in cards and matrix (empty = all). Persisted.
  const [matrixCustomers, setMatrixCustomers] = useState<string[]>(loadMatrixCustomers);
  // Matrix year columns the user chose to hide. Persisted.
  const [hiddenCols, setHiddenCols] = useState<string[]>(() => loadStringList(MATRIX_HIDDEN_COLS_KEY));

  const [editorOpen, setEditorOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<string | null>(null);
  const [editMonth, setEditMonth] = useState<string>(`${CURRENT_YEAR}-${String(CURRENT_MONTH).padStart(2, "0")}`);
  const [editQty, setEditQty] = useState<number | string>(0);
  const [editPlanId, setEditPlanId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  async function loadCustomers() {
    try {
      setCustomers(await api.listCustomers());
    } catch {
      // soft-fail
    }
  }

  async function load() {
    setLoading(true);
    try {
      const res = await api.listDeliveryPlans(year, undefined, Number(productTypeId));
      setRows(res.rows);
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({ color: "red", message: detail.slice(0, 200) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadCustomers();
  }, []);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, productTypeId]);

  const monthName = (m: number) =>
    new Intl.DateTimeFormat(i18n.language, { month: "long" }).format(new Date(2000, m - 1, 1));
  const monthShort = (m: number) =>
    new Intl.DateTimeFormat(i18n.language, { month: "short" }).format(new Date(2000, m - 1, 1));

  const monthKey = `${year}-${String(month).padStart(2, "0")}`;

  const customerOptions = customers.map((c) => ({ value: String(c.id), label: c.name }));
  const isShown = (cid: number) => matrixCustomers.length === 0 || matrixCustomers.includes(String(cid));
  const yearOptions = useMemo(
    () =>
      [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => ({
        value: String(y),
        label: String(y),
      })),
    [],
  );
  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: String(i + 1),
        label: new Intl.DateTimeFormat(i18n.language, { month: "long" }).format(new Date(2000, i, 1)),
      })),
    [i18n.language],
  );

  // Index rows by (customer, month) for the matrix; group by customer for the expander.
  const byKey = useMemo(() => {
    const m = new Map<string, DeliveryPlanRow>();
    for (const r of rows) m.set(`${r.customer_id}-${r.period_month}`, r);
    return m;
  }, [rows]);

  const monthsByCustomer = useMemo(() => {
    const map = new Map<number, DeliveryPlanRow[]>();
    for (const r of rows) {
      const arr = map.get(r.customer_id);
      if (arr) arr.push(r);
      else map.set(r.customer_id, [r]);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.period_month.localeCompare(b.period_month));
    return map;
  }, [rows]);

  // Distinct customers with any plan/delivery in the year, sorted by name (matrix rows).
  const customersInYear = useMemo(() => {
    const names = new Map<number, string | null>();
    for (const r of rows) if (!names.has(r.customer_id)) names.set(r.customer_id, r.customer_name);
    return [...names.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [rows]);

  // Persist + apply the matrix customer filter (empty selection = show all).
  function updateMatrixCustomers(ids: string[]) {
    setMatrixCustomers(ids);
    try {
      localStorage.setItem(MATRIX_CUSTOMERS_KEY, JSON.stringify(ids));
    } catch {
      // ignore
    }
  }
  const shownCustomers = useMemo(() => {
    if (matrixCustomers.length === 0) return customersInYear;
    const set = new Set(matrixCustomers);
    return customersInYear.filter((c) => set.has(String(c.id)));
  }, [customersInYear, matrixCustomers]);

  const yearRemaining = (cid: number) =>
    (monthsByCustomer.get(cid) ?? []).reduce((sum, r) => sum + r.remaining, 0);
  const yearDelivered = (cid: number) =>
    (monthsByCustomer.get(cid) ?? []).reduce((sum, r) => sum + r.billed, 0);
  const yearBreakdown = (cid: number) => sumBreakdown(monthsByCustomer.get(cid) ?? []);
  const shownYearBreakdown = useMemo(
    () => sumBreakdown(shownCustomers.flatMap((c) => monthsByCustomer.get(c.id) ?? [])),
    [shownCustomers, monthsByCustomer],
  );
  // Column total for the TOTAL row: Σ delivered/planned over the SHOWN customers.
  function matrixColTotal(mNum: number) {
    const key = `${year}-${String(mNum).padStart(2, "0")}`;
    let delivered = 0;
    let planned = 0;
    let exchange = 0;
    for (const c of shownCustomers) {
      const r = byKey.get(`${c.id}-${key}`);
      if (r) { delivered += r.billed; planned += r.planned; exchange += r.gross_exchange; }
    }
    return { delivered, planned, exchange };
  }

  // A month counts when it has a plan or any activity (shipped, returned, moved, foreign).
  const hasActivity = (r: DeliveryPlanRow) =>
    r.planned > 0 || r.planned_original != null || r.gross > 0 || r.moved_out > 0 || r.foreign_returned > 0;

  // Matrix columns: months with a plan or a delivery for some customer.
  const plannedMonths = useMemo(() => {
    const set = new Set<number>();
    for (const r of rows) if (isShown(r.customer_id) && hasActivity(r)) set.add(Number(r.period_month.slice(5, 7)));
    return [...set].sort((a, b) => a - b);
  }, [rows, matrixCustomers]);

  // Cards: rows in the selected month with a plan or a delivery.
  const cards = useMemo(
    () =>
      rows
        .filter((r) => r.period_month === monthKey && isShown(r.customer_id) && hasActivity(r))
        .sort((a, b) => (a.customer_name ?? "").localeCompare(b.customer_name ?? "")),
    [rows, monthKey, matrixCustomers],
  );

  // Totals for the selected month, across the shown customers: real new deliveries
  // (without defect-exchange) and including defect-exchange replacements.
  const monthTotals = useMemo(() => {
    const b = sumBreakdown(rows.filter((r) => r.period_month === monthKey && isShown(r.customer_id)));
    return {
      withoutExchange: b.gross_normal,
      withExchange: b.gross_normal + b.gross_exchange,
      exchange: b.gross_exchange,
      b,
    };
  }, [rows, monthKey, matrixCustomers]);

  function toggleExpand(cid: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid);
      else next.add(cid);
      return next;
    });
  }

  function openAdd() {
    setEditCustomer(matrixCustomers.length === 1 ? matrixCustomers[0] : null);
    setEditMonth(monthKey);
    setEditQty(0);
    setEditPlanId(null);
    setEditorOpen(true);
  }

  function openEdit(r: DeliveryPlanRow) {
    setEditCustomer(String(r.customer_id));
    setEditMonth(r.period_month);
    setEditQty(r.planned);
    setEditPlanId(r.plan_id);
    setEditorOpen(true);
  }

  function openCell(cid: number, key: string) {
    const row = byKey.get(`${cid}-${key}`);
    if (row) {
      openEdit(row);
      return;
    }
    setEditCustomer(String(cid));
    setEditMonth(key);
    setEditQty(0);
    setEditPlanId(null);
    setEditorOpen(true);
  }

  async function save() {
    if (!editCustomer || !editMonth) {
      notifications.show({ color: "red", message: t("shipmentStats.plan.pickCustomerMonth") });
      return;
    }
    setSaving(true);
    try {
      await api.upsertDeliveryPlan({
        customer_id: Number(editCustomer),
        period_month: editMonth,
        planned_qty: Number(editQty) || 0,
        product_type_id: Number(productTypeId),
      });
      notifications.show({ color: "green", message: t("shipmentStats.plan.savedToast") });
      setEditorOpen(false);
      void load();
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({ color: "red", message: detail.slice(0, 200) });
    } finally {
      setSaving(false);
    }
  }

  async function deleteEditing() {
    if (editPlanId == null) return;
    if (!window.confirm(t("shipmentStats.plan.confirmDelete"))) return;
    setSaving(true);
    try {
      await api.deleteDeliveryPlan(editPlanId);
      notifications.show({ color: "green", message: t("shipmentStats.plan.deletedToast") });
      setEditorOpen(false);
      void load();
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({ color: "red", message: detail.slice(0, 200) });
    } finally {
      setSaving(false);
    }
  }

  async function remove(r: DeliveryPlanRow) {
    if (r.plan_id == null) return;
    if (!window.confirm(t("shipmentStats.plan.confirmDelete"))) return;
    try {
      await api.deleteDeliveryPlan(r.plan_id);
      notifications.show({ color: "green", message: t("shipmentStats.plan.deletedToast") });
      void load();
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({ color: "red", message: detail.slice(0, 200) });
    }
  }

  // Year columns of the matrix: how the net "delivered" total is built.
  const allYearCols: { key: keyof ReturnType<typeof sumBreakdown>; label: string; color?: string }[] = [
    { key: "gross", label: t("shipmentStats.plan.bd.grossShort") },
    { key: "gross_normal", label: t("shipmentStats.plan.bd.normalShort") },
    { key: "gross_exchange", label: t("shipmentStats.plan.bd.exchangeShort") },
    { key: "delivered_debt", label: t("shipmentStats.plan.bd.debtShort"), color: "orange" },
    { key: "returned", label: t("shipmentStats.plan.bd.returnedShort"), color: "red" },
    { key: "moved_out", label: t("shipmentStats.plan.bd.movedOutShort"), color: "grape" },
    { key: "foreign_returned", label: t("shipmentStats.plan.bd.foreignShort"), color: "grape" },
  ];
  const yearCols = allYearCols.filter((c) => !hiddenCols.includes(c.key));
  // Per-month exchange line under each matrix cell follows the "Schimb" column.
  const showCellExchange = !hiddenCols.includes("gross_exchange");
  const exchangeLabel = t("shipmentStats.plan.bd.exchangeShort").toLowerCase();
  const originalLabel = t("shipmentStats.plan.originalShort");
  function updateHiddenCols(shown: string[]) {
    const hidden = allYearCols.map((c) => c.key as string).filter((k) => !shown.includes(k));
    setHiddenCols(hidden);
    try {
      localStorage.setItem(MATRIX_HIDDEN_COLS_KEY, JSON.stringify(hidden));
    } catch {
      // ignore
    }
  }

  function cellColor(row: DeliveryPlanRow | undefined): string | undefined {
    if (!row) return "dimmed";
    if (row.remaining > 0) return "red";
    if (row.planned > 0 && row.billed >= row.planned) return "teal";
    return undefined;
  }

  return (
    <Stack>
      <Card withBorder>
        <Group align="end" gap="sm" wrap="wrap">
          <SegmentedControl
            value={productTypeId}
            onChange={setProductTypeId}
            data={[
              { value: "1", label: t("shipmentStats.plan.typeModule") },
              { value: "2", label: t("shipmentStats.plan.typeAntenna") },
            ]}
          />
          <Select
            label={t("shipmentStats.plan.year")}
            data={yearOptions}
            value={String(year)}
            onChange={(v) => setYear(Number(v) || CURRENT_YEAR)}
            allowDeselect={false}
            w={110}
          />
          <SegmentedControl
            value={view}
            onChange={(v) => setView(v as "cards" | "matrix")}
            data={[
              { value: "cards", label: t("shipmentStats.plan.viewCards") },
              { value: "matrix", label: t("shipmentStats.plan.viewMatrix") },
            ]}
          />
          {view === "cards" && (
            <Select
              label={t("shipmentStats.plan.month")}
              data={monthOptions}
              value={String(month)}
              onChange={(v) => setMonth(Number(v) || CURRENT_MONTH)}
              allowDeselect={false}
              w={150}
            />
          )}
          <MultiSelect
            label={t("shipmentStats.plan.matrixCustomers")}
            placeholder={matrixCustomers.length === 0 ? t("shipmentStats.plan.matrixCustomersAll") : undefined}
            data={customerOptions}
            value={matrixCustomers}
            onChange={updateMatrixCustomers}
            searchable
            clearable
            hidePickedOptions
            w={320}
            maw="100%"
          />
          {view === "matrix" && (
            <MultiSelect
              label={t("shipmentStats.plan.matrixColumns")}
              placeholder={yearCols.length === 0 ? t("shipmentStats.plan.matrixColumnsNone") : undefined}
              data={allYearCols.map((c) => ({ value: c.key, label: c.label }))}
              value={yearCols.map((c) => c.key)}
              onChange={updateHiddenCols}
              clearable
              w={320}
            />
          )}
          <Button leftSection={<IconPlus size={14} />} onClick={openAdd}>
            {t("shipmentStats.plan.addPlan")}
          </Button>
        </Group>
      </Card>

      {view === "cards" && !loading && (
        <Card withBorder padding="sm">
          <Group gap="xl" wrap="wrap">
            <div>
              <Text size="xs" c="dimmed">
                {t("shipmentStats.plan.totalDelivered", { month: monthName(month) })}
              </Text>
              <Text fw={700} size="xl">{monthTotals.withoutExchange}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">{t("shipmentStats.plan.totalWithExchange")}</Text>
              <Text fw={700} size="xl" c="grape">{monthTotals.withExchange}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">{t("shipmentStats.plan.totalExchange")}</Text>
              <Text fw={600} size="lg" c="dimmed">{monthTotals.exchange}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">{t("shipmentStats.plan.bd.gross")}</Text>
              <Text fw={600} size="lg">{monthTotals.b.gross}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">{t("shipmentStats.plan.bd.returnedShort")}</Text>
              <Text fw={600} size="lg" c={monthTotals.b.returned > 0 ? "red" : "dimmed"}>{monthTotals.b.returned}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">{t("shipmentStats.plan.bd.debtShort")}</Text>
              <Text fw={600} size="lg" c={monthTotals.b.delivered_debt > 0 ? "orange" : "dimmed"}>{monthTotals.b.delivered_debt}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">{t("shipmentStats.plan.bd.movedOutShort")}</Text>
              <Text fw={600} size="lg" c={monthTotals.b.moved_out > 0 ? "grape" : "dimmed"}>{monthTotals.b.moved_out}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">{t("shipmentStats.plan.bd.foreignShort")}</Text>
              <Text fw={600} size="lg" c={monthTotals.b.foreign_returned > 0 ? "grape" : "dimmed"}>{monthTotals.b.foreign_returned}</Text>
            </div>
          </Group>
        </Card>
      )}

      {loading ? (
        <Loader />
      ) : view === "cards" ? (
        cards.length === 0 ? (
          <Text c="dimmed" size="sm">
            {t("shipmentStats.plan.emptyMonth")}
          </Text>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="sm">
            {cards.map((r) => {
              const hasPlan = r.planned > 0 || r.planned_original != null;
              const reached = hasPlan && r.billed >= r.planned;
              const pct = r.planned > 0 ? Math.min(100, (r.billed / r.planned) * 100) : hasPlan ? 100 : 0;
              const isOpen = expanded.has(r.customer_id);
              const months = monthsByCustomer.get(r.customer_id) ?? [];
              return (
                <Card key={r.customer_id} withBorder padding="sm">
                  <Group justify="space-between" mb={2} wrap="nowrap" gap="xs">
                    <Text fw={600} size="sm" lineClamp={1}>
                      {r.customer_name ?? `#${r.customer_id}`}
                    </Text>
                    <Button size="compact-xs" variant="subtle" onClick={() => openEdit(r)}>
                      <IconPencil size={14} />
                    </Button>
                  </Group>
                  <Text size="xs" c="dimmed" mb={6}>
                    {monthName(month)} {year}
                  </Text>

                  {hasPlan ? (
                    <>
                      <Group justify="space-between" mb={2}>
                        <Text size="sm" ff="monospace">
                          {r.billed}/{r.planned}
                          {r.planned_original != null && (
                            <Text span size="xs" c="dimmed"> ({originalLabel} {r.planned_original})</Text>
                          )}
                        </Text>
                        {reached && (
                          <Badge color="teal" variant="light" size="sm">
                            ✓
                          </Badge>
                        )}
                      </Group>
                      <Progress value={pct} color={reached ? "teal" : "blue"} size="md" radius="sm" />
                    </>
                  ) : (
                    <Text size="sm">
                      {t("shipmentStats.plan.colBilled")}: {r.billed} · {t("shipmentStats.plan.noPlan")}
                    </Text>
                  )}

                  <Card withBorder padding={6} mt={6} radius="sm">
                    <PlanBreakdown b={r} />
                  </Card>

                  <Text mt={6} size="sm" fw={600} c={r.remaining > 0 ? "red" : "teal"}>
                    {t("shipmentStats.plan.colRemaining")}: {r.remaining}
                  </Text>

                  <Button
                    variant="subtle"
                    size="compact-xs"
                    mt="xs"
                    leftSection={isOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                    onClick={() => toggleExpand(r.customer_id)}
                  >
                    {t("shipmentStats.plan.allMonths")}
                  </Button>
                  <Collapse in={isOpen}>
                    <ResponsiveTable
                      mobile={{
                        rows: months,
                        rowKey: (mr) => mr.period_month,
                        title: (mr) => mr.period_month,
                        subtitle: (mr) => `${mr.billed}/${mr.planned}`,
                        badge: (mr) => <Text span size="sm" fw={600} c={mr.remaining > 0 ? "red" : "teal"}>{mr.remaining}</Text>,
                        fields: [
                          { label: t("shipmentStats.plan.colPlanned"), render: (mr) => <PlannedCell r={mr} label={originalLabel} /> },
                          { label: t("shipmentStats.plan.bd.grossShort"), render: (mr) => mr.gross },
                          { label: t("shipmentStats.plan.bd.exchangeShort"), render: (mr) => mr.gross_exchange },
                          { label: t("shipmentStats.plan.bd.returnedShort"), render: (mr) => <Text span c={mr.returned > 0 ? "red" : "dimmed"}>{mr.returned}</Text> },
                          { label: t("shipmentStats.plan.colDelivered"), render: (mr) => mr.delivered },
                          { label: t("shipmentStats.plan.colBilled"), render: (mr) => <b>{mr.billed}</b> },
                          { label: t("shipmentStats.plan.bd.movedOutShort"), render: (mr) => <Text span c={mr.moved_out > 0 ? "grape" : "dimmed"}>{mr.moved_out}</Text> },
                          { label: t("shipmentStats.plan.colDebt"), render: (mr) => <Text span c={mr.delivered_debt > 0 ? "orange" : "dimmed"}>{mr.delivered_debt}</Text> },
                          { label: t("shipmentStats.plan.colRemaining"), render: (mr) => <Text span c={mr.remaining > 0 ? "red" : "teal"}>{mr.remaining}</Text> },
                        ],
                        actions: (mr) => (
                          <Group gap="xs" wrap="nowrap">
                            <Button size="compact-md" variant="subtle" onClick={() => openEdit(mr)}>
                              <IconPencil size={16} />
                            </Button>
                            {mr.plan_id != null && (
                              <Button size="compact-md" variant="subtle" color="red" onClick={() => void remove(mr)}>
                                <IconTrash size={16} />
                              </Button>
                            )}
                          </Group>
                        ),
                      }}
                    >
                    <Table.ScrollContainer minWidth={560}>
                      <Table mt="xs" withRowBorders={false}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>{t("shipmentStats.plan.colMonth")}</Table.Th>
                          <Table.Th>{t("shipmentStats.plan.colPlanned")}</Table.Th>
                          <Table.Th>{t("shipmentStats.plan.bd.grossShort")}</Table.Th>
                          <Table.Th>{t("shipmentStats.plan.bd.exchangeShort")}</Table.Th>
                          <Table.Th>{t("shipmentStats.plan.bd.returnedShort")}</Table.Th>
                          <Table.Th>{t("shipmentStats.plan.colDelivered")}</Table.Th>
                          <Table.Th>{t("shipmentStats.plan.colBilled")}</Table.Th>
                          <Table.Th>{t("shipmentStats.plan.bd.movedOutShort")}</Table.Th>
                          <Table.Th>{t("shipmentStats.plan.colDebt")}</Table.Th>
                          <Table.Th>{t("shipmentStats.plan.colRemaining")}</Table.Th>
                          <Table.Th w={70} />
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {months.map((mr) => (
                          <Table.Tr key={mr.period_month}>
                            <Table.Td>{mr.period_month}</Table.Td>
                            <Table.Td><PlannedCell r={mr} label={originalLabel} /></Table.Td>
                            <Table.Td>{mr.gross}</Table.Td>
                            <Table.Td>{mr.gross_exchange}</Table.Td>
                            <Table.Td>
                              <Text c={mr.returned > 0 ? "red" : "dimmed"}>{mr.returned}</Text>
                            </Table.Td>
                            <Table.Td>{mr.delivered}</Table.Td>
                            <Table.Td fw={700}>{mr.billed}</Table.Td>
                            <Table.Td>
                              <Text c={mr.moved_out > 0 ? "grape" : "dimmed"}>{mr.moved_out}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text c={mr.delivered_debt > 0 ? "orange" : "dimmed"}>{mr.delivered_debt}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Text c={mr.remaining > 0 ? "red" : "teal"}>{mr.remaining}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Group gap={2} wrap="nowrap">
                                <Button size="compact-xs" variant="subtle" onClick={() => openEdit(mr)}>
                                  <IconPencil size={12} />
                                </Button>
                                {mr.plan_id != null && (
                                  <Button
                                    size="compact-xs"
                                    variant="subtle"
                                    color="red"
                                    onClick={() => void remove(mr)}
                                  >
                                    <IconTrash size={12} />
                                  </Button>
                                )}
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                    </ResponsiveTable>
                  </Collapse>
                </Card>
              );
            })}
          </SimpleGrid>
        )
      ) : customersInYear.length === 0 ? (
        <Text c="dimmed" size="sm">
          {t("shipmentStats.plan.emptyMonth")}
        </Text>
      ) : (
        <ResponsiveTable
          forceMode="sticky"
          stickyColumns={1}
          mobile={{
            rows: shownCustomers,
            rowKey: (c) => c.id,
            title: (c) => c.name ?? `#${c.id}`,
            fields: [
              ...plannedMonths.map((mNum) => ({
                label: monthShort(mNum),
                render: (c: { id: number; name: string | null }) => {
                  const row = byKey.get(`${c.id}-${year}-${String(mNum).padStart(2, "0")}`);
                  if (!row) return "–";
                  let main = row.planned > 0 ? `${row.billed}/${row.planned}` : String(row.billed);
                  if (showCellExchange && row.gross_exchange > 0) main += ` · ${exchangeLabel} ${row.gross_exchange}`;
                  if (row.planned_original != null) main += ` · ${originalLabel} ${row.planned_original}`;
                  return main;
                },
              })),
              ...yearCols.map((col) => ({
                label: col.label,
                render: (c: { id: number; name: string | null }) => yearBreakdown(c.id)[col.key],
              })),
              { label: t("shipmentStats.plan.yearDelivered"), render: (c: { id: number; name: string | null }) => yearDelivered(c.id) },
              { label: t("shipmentStats.plan.yearRemaining"), render: (c: { id: number; name: string | null }) => yearRemaining(c.id) },
            ],
          }}
        >
        <Table.ScrollContainer minWidth={900}>
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            fz="xs"
            verticalSpacing={3}
            horizontalSpacing={6}
            layout="auto"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("shipmentStats.plan.colCustomer")}</Table.Th>
                {plannedMonths.map((mNum) => (
                  <Table.Th key={mNum} style={{ textAlign: "center" }}>
                    {monthShort(mNum)}
                  </Table.Th>
                ))}
                {yearCols.map((col) => (
                  <Table.Th key={col.key} style={{ textAlign: "right", whiteSpace: "nowrap" }}>{col.label}</Table.Th>
                ))}
                <Table.Th style={{ textAlign: "right" }}>{t("shipmentStats.plan.yearDelivered")}</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>{t("shipmentStats.plan.yearRemaining")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {shownCustomers.map((c) => {
                const yr = yearRemaining(c.id);
                return (
                  <Table.Tr key={c.id}>
                    <Table.Td>{c.name ?? `#${c.id}`}</Table.Td>
                    {plannedMonths.map((mNum) => {
                      const key = `${year}-${String(mNum).padStart(2, "0")}`;
                      const row = byKey.get(`${c.id}-${key}`);
                      return (
                        <Table.Td
                          key={mNum}
                          style={{ textAlign: "center", cursor: "pointer", whiteSpace: "nowrap" }}
                          onClick={() => openCell(c.id, key)}
                        >
                          {row ? (
                            <Tooltip
                              withArrow
                              openDelay={150}
                              w={260}
                              color="var(--mantine-color-body)"
                              styles={{ tooltip: { color: "var(--mantine-color-text)", border: "1px solid var(--mantine-color-default-border)" } }}
                              label={<PlanBreakdown b={row} />}
                            >
                              <Text size="xs" c={cellColor(row)}>
                                {row.planned > 0 ? `${row.billed}/${row.planned}` : row.billed}
                                {row.returned > 0 && (
                                  <Text span size="xs" c="red"> −{row.returned}</Text>
                                )}
                              </Text>
                            </Tooltip>
                          ) : (
                            <Text size="xs" c="dimmed">–</Text>
                          )}
                          {row && row.planned_original != null && (
                            <Text size="xs" c="dimmed" lh={1.1} title={t("shipmentStats.plan.originalHint")}>
                              {originalLabel} {row.planned_original}
                            </Text>
                          )}
                          {showCellExchange && row && row.gross_exchange > 0 && (
                            <Text size="xs" c="grape" lh={1.1}>
                              {exchangeLabel} {row.gross_exchange}
                            </Text>
                          )}
                          {row && row.delivered_debt > 0 && (
                            <Text size="xs" c="orange" lh={1.1} title={t("shipmentStats.plan.colDebt")}>
                              +{row.delivered_debt} {t("shipmentStats.plan.debtShort")}
                            </Text>
                          )}
                        </Table.Td>
                      );
                    })}
                    {yearCols.map((col) => {
                      const v = yearBreakdown(c.id)[col.key];
                      return (
                        <Table.Td key={col.key} style={{ textAlign: "right" }}>
                          <Text size="xs" c={v > 0 ? col.color : "dimmed"}>{v}</Text>
                        </Table.Td>
                      );
                    })}
                    <Table.Td style={{ textAlign: "right" }}>
                      <Text fw={700}>{yearDelivered(c.id)}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Text fw={600} c={yr > 0 ? "red" : "teal"}>
                        {yr}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              <Table.Tr style={{ borderTop: "2px solid var(--mantine-color-default-border)" }}>
                <Table.Td><Text fw={700}>{t("shipmentStats.plan.totalRow")}</Text></Table.Td>
                {plannedMonths.map((mNum) => {
                  const tot = matrixColTotal(mNum);
                  return (
                    <Table.Td key={mNum} style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      <Text fw={700} size="xs">{tot.delivered}/{tot.planned}</Text>
                      {showCellExchange && tot.exchange > 0 && (
                        <Text fw={700} size="xs" c="grape" lh={1.1}>
                          {exchangeLabel} {tot.exchange}
                        </Text>
                      )}
                    </Table.Td>
                  );
                })}
                {yearCols.map((col) => (
                  <Table.Td key={col.key} style={{ textAlign: "right" }}>
                    <Text fw={700} size="xs">{shownYearBreakdown[col.key]}</Text>
                  </Table.Td>
                ))}
                <Table.Td style={{ textAlign: "right" }}>
                  <Text fw={700}>
                    {shownCustomers.reduce((s, c) => s + yearDelivered(c.id), 0)}
                  </Text>
                </Table.Td>
                <Table.Td style={{ textAlign: "right" }}>
                  <Text fw={700}>
                    {shownCustomers.reduce((s, c) => s + yearRemaining(c.id), 0)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        </ResponsiveTable>
      )}

      <Modal
        opened={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={t("shipmentStats.plan.editorTitle")}
        centered
      >
        <Stack>
          <Select
            label={t("shipmentStats.plan.customerLabel")}
            data={customers.map((c) => ({ value: String(c.id), label: c.name }))}
            value={editCustomer}
            onChange={setEditCustomer}
            searchable
          />
          <TextInput
            type="month"
            label={t("shipmentStats.plan.monthLabel")}
            value={editMonth}
            onChange={(e) => setEditMonth(e.currentTarget.value)}
          />
          <NumberInput
            label={t("shipmentStats.plan.qtyLabel")}
            value={editQty}
            onChange={setEditQty}
            min={0}
            allowNegative={false}
          />
          <Group justify="space-between">
            {editPlanId != null ? (
              <Button
                color="red"
                variant="light"
                leftSection={<IconTrash size={14} />}
                loading={saving}
                onClick={() => void deleteEditing()}
              >
                {t("shipmentStats.plan.delete")}
              </Button>
            ) : (
              <span />
            )}
            <Group gap="xs">
              <Button variant="subtle" onClick={() => setEditorOpen(false)}>
                {t("common.cancel")}
              </Button>
              <Button loading={saving} onClick={() => void save()}>
                {t("common.save")}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function PlannedCell({ r, label }: { r: DeliveryPlanRow; label: string }) {
  return (
    <>
      {r.planned}
      {r.planned_original != null && (
        <Text span size="xs" c="dimmed"> ({label} {r.planned_original})</Text>
      )}
    </>
  );
}
